import { useTranslations } from '@vocab/react';
import {
  Alert,
  Box,
  Column,
  Columns,
  Heading,
  Loader,
  PageBlock,
  Stack,
  Text,
} from 'braid-design-system';

import { Nav } from 'src/components/Nav/Nav';
import { PageWrapper } from 'src/components/PageWrapper/PageWrapper';
import type { PageProps } from 'src/types';
import { useIntegrationPartners } from 'src/utils/hooks/useIntegrationPartners';
import { useQueryHistory } from 'src/utils/hooks/useQueryHistory';
import { chooseConnectedPartners } from 'src/utils/partners';
import { slugify } from 'src/utils/slugify';

import translations from '../LinkYourSoftware/.vocab';

import { PageIntro } from './components/PageIntro/PageIntro';
import { PartnerList } from './components/PartnerList/PartnerList';
import { SearchFilter } from './components/SearchFilter/SearchFilter';
import {
  groupPartners,
  makePartnerSuggestions,
} from './components/SearchFilter/makePartnerSuggestions';
import { SolutionsFilter } from './components/SolutionsFilter/SolutionsFilter';

export const ConnectedPartners = ({ slug }: PageProps) => {
  const { t } = useTranslations(translations);
  const pageTrackingProps = {
    page: `${slugify({ str: slug })}`,
    eventName: `${slugify({ str: slug })}-viewed`,
    siteSection: 'partners',
  };

  const connectedPartners = chooseConnectedPartners(
    useIntegrationPartners().partners,
  );

  const solutionsQuery = useQueryHistory('solutions');
  const searchQuery = useQueryHistory('name');
  const solutionFilter = solutionsQuery.values;
  const searchFilter = searchQuery.value || '';

  return (
    <PageWrapper
      pageTrackingProps={pageTrackingProps}
      title={slug}
      footerPosition="belowFold"
    >
      <Nav activeTab="connected-partners" />
      <PageBlock width="large">
        <PageIntro />
        <Box
          width="full"
          paddingTop="medium"
          paddingBottom="xxlarge"
          paddingX="gutter"
        >
          <Columns space="large" collapseBelow="tablet">
            <Column width="1/3">
              <Stack space="medium">
                <Heading level="4">Filters</Heading>

                <SolutionsFilter disabled={searchFilter !== ''} />
                <Text tone="secondary">OR</Text>
                {connectedPartners.state === 'data' && (
                  <SearchFilter
                    disabled={false}
                    groupedSuggestions={makePartnerSuggestions(
                      connectedPartners.data,
                    )}
                  />
                )}
              </Stack>
            </Column>
            <Column>
              {connectedPartners.state === 'data' ? (
                <>
                  {connectedPartners.data.length > 0 ? (
                    <PartnerList
                      data={groupPartners(connectedPartners.data)}
                      searchFilter={searchFilter}
                      solutionFilter={solutionFilter}
                    />
                  ) : null}
                </>
              ) : (
                <>
                  {connectedPartners.state === 'loading' ? (
                    <Loader delayVisibility />
                  ) : (
                    <Alert tone="critical">
                      <Text>{t('somethingWentWrong')}</Text>
                    </Alert>
                  )}
                </>
              )}
            </Column>
          </Columns>
        </Box>
      </PageBlock>
    </PageWrapper>
  );
};
